/**
 * SettlementPreviewDetails Component
 *
 * Preview-only container for settlement details
 * Uses SettlementDetails for UI + adds Print button only
 * No edit capabilities, read-only view
 */

import React, { useState, useEffect } from "react";
import { HiPrinter } from "react-icons/hi";
import nodeinstance from "../../../../../nodeInstance";
import SettlementDetails from "../SettlementDetails/SettlementDetails";

const SettlementPreviewDetails = ({
  employeeID,
  isLocalExit = false,
}) => {
  // State Management
  const [data, setData] = useState();
  const [trigger, setTrigger] = useState(false);
  const [repatriationAmount, setRepatriationAmount] = useState(0);
  const [repatriationSaved, setRepatriationSaved] = useState(true); // Always true in preview
  const [selectedRepatrationType, setSelectedRepatrationType] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const token = localStorage.getItem("nodeToken");
  const baseEndpoint = isLocalExit ? '/localexit' : '/mobilization';

  // Fetch employee data
  const fetchEmployeeData = async () => {
    try {
      const endpoint = isLocalExit
        ? `/localexit/localExitFormalities/${employeeID}`
        : `/mobilization/finalExitFormalities/${employeeID}`;

      const response = await nodeinstance.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setData(response.data.data);

        // Initialize repatriation state from saved data
        const repatrationData = response.data.data;
        if (repatrationData?.data?.[0]?.repatrationAmount) {
          setRepatriationAmount(repatrationData.data[0].repatrationAmount);
        }
        if (repatrationData?.repatrationType) {
          setSelectedRepatrationType(repatrationData.repatrationType);
        }
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  useEffect(() => {
    fetchEmployeeData();
  }, [employeeID]);

  // Print Handler
  const handlePrint = () => {
    setIsPrinting(true);

    // Hide print button during print
    const printButton = document.getElementById('print-button-container');
    if (printButton) {
      printButton.style.display = 'none';
    }

    // Trigger browser print dialog
    window.print();

    // Show print button again after print dialog closes
    setTimeout(() => {
      if (printButton) {
        printButton.style.display = 'block';
      }
      setIsPrinting(false);
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Page Header */}
      <div className="mb-6 bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Settlement Preview
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Employee ID: <span className="font-semibold">{data?.data?.[0]?.candidate_id || employeeID}</span>
            </p>
            <p className="text-sm text-gray-600">
              Name: <span className="font-semibold">{data?.data?.[0]?.name || "Loading..."}</span>
            </p>
          </div>

          {/* Status Badge */}
          {data?.Final_exit_status && (
            <div className="flex items-center space-x-2">
              <span className="text-xs font-semibold text-gray-600 uppercase">Status:</span>
              <span className={`px-4 py-2 rounded-lg text-sm font-bold uppercase ${
                data.Final_exit_status === "OPSAUTHORISED"
                  ? "bg-green-100 text-green-800"
                  : "bg-blue-100 text-blue-800"
              }`}>
                {data.Final_exit_status}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Settlement Details UI (Editable) */}
      <SettlementDetails
        employeeID={employeeID}
        data={data}
        trigger={trigger}
        setTrigger={setTrigger}
        isLocalExit={isLocalExit}
        baseEndpoint={baseEndpoint}
        repatration={null}
        updateNew={null}
        repatriationAmount={repatriationAmount}
        setRepatriationAmount={setRepatriationAmount}
        repatriationSaved={repatriationSaved}
        setRepatriationSaved={setRepatriationSaved}
        selectedRepatrationType={selectedRepatrationType}
        setSelectedRepatrationType={setSelectedRepatrationType}
        fandftrigger={false}
        setFandfTrigger={() => {}}
        isPreviewMode={false} // Enable editing (same as FinalExitDetails)
      />

      {/* Print Button */}
      <div id="print-button-container" className="mt-8 bg-white rounded-lg shadow-lg p-6 border border-gray-200 print:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-800 uppercase">
              Ready to Print?
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Click the button to print or save as PDF
            </p>
          </div>

          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="inline-flex items-center px-8 py-4 bg-btn-red text-white font-bold rounded-lg hover:bg-btn-red-hover focus:outline-none focus:ring-4 focus:ring-red-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            <HiPrinter className="w-6 h-6 mr-3" />
            {isPrinting ? "PREPARING..." : "PRINT STATEMENT"}
          </button>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            background: white !important;
          }

          .print\\:hidden {
            display: none !important;
          }

          /* Remove backgrounds for better print quality */
          .bg-gray-50,
          .bg-gray-100,
          .bg-blue-50 {
            background: white !important;
          }

          /* Ensure proper page breaks */
          .space-y-6 > * {
            page-break-inside: avoid;
          }

          /* Remove shadows and borders for cleaner print */
          .shadow-sm,
          .shadow-md,
          .shadow-lg,
          .shadow-xl {
            box-shadow: none !important;
          }

          .border {
            border: 1px solid #e5e7eb !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SettlementPreviewDetails;
